import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useTranslation } from '@tyro/i18n';
import { toCssAspectRatio } from '../../utils/video-format';

interface VideoData {
  embedUrl: string;
  aspectRatio?: string | null;
}

interface VideoHeroBlockProps {
  videoData: VideoData;
  title: string;
}

const VIDEO_HOST_ALLOWLIST = [
  '.loom.com',
  '.youtube.com',
  '.youtube-nocookie.com',
  'youtu.be',
  '.vimeo.com',
];

export function isAllowedVideoHost(embedUrl: string): boolean {
  try {
    const host = new URL(embedUrl).hostname.toLowerCase();
    return VIDEO_HOST_ALLOWLIST.some((suffix) =>
      suffix.startsWith('.')
        ? host === suffix.slice(1) || host.endsWith(suffix)
        : host === suffix,
    );
  } catch {
    return false;
  }
}

export function VideoHeroBlock({ videoData, title }: VideoHeroBlockProps) {
  const { t } = useTranslation(['documentation']);

  if (!isAllowedVideoHost(videoData.embedUrl)) {
    return (
      <Alert severity="error" variant="outlined">
        {t('documentation:editor.video.allowlistError')}
      </Alert>
    );
  }

  const aspectRatio = toCssAspectRatio(videoData.aspectRatio);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio,
        overflow: 'hidden',
        bgcolor: 'common.black',
      }}
    >
      <iframe
        src={videoData.embedUrl}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </Box>
  );
}
